import { keyframes } from "@emotion/react";
import { TFunction } from "i18next";
import { CheckCircleIcon, XIcon } from "lucide-react";
import { ComponentPropsWithoutRef, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { QrReader } from "react-qr-reader";
import { toast } from "sonner";
import tw from "twin.macro";

import { useAreas } from "@/api/areas/queries";

const showFailureToast = (
  translationKey: string,
  translator: TFunction<"translation", undefined>
) => {
  toast.custom(
    (toastId) => (
      <div
        role="status"
        tw="flex items-start justify-center gap-3 rounded-lg bg-red-500 px-4 py-3"
      >
        <div tw="flex shrink-0 bg-white items-center justify-center rounded-full w-8 h-8 self-center">
          <XIcon tw="w-5 h-5 text-red-500" />
        </div>
        <div tw="flex flex-1 flex-col gap-1 min-w-0">
          <p tw="text-lg font-bold text-white">
            {translator("quickModuleAccess.error")}
          </p>
          <p tw="text-base font-medium text-white">
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {translator(translationKey as any)}
          </p>
        </div>
        <button
          type="button"
          aria-label={translator("global.close")}
          onClick={() => toast.dismiss(toastId)}
          tw="shrink-0 rounded p-1 text-white hover:bg-red-600 focus:(outline-none ring-2 ring-offset-1 ring-red-600)"
        >
          <XIcon tw="w-4 h-4" />
        </button>
      </div>
    ),
    {
      duration: Infinity,
      id: "quick-module-access-failure",
      position: "top-right",
    }
  );
};

const greenBorderFade = keyframes({
  from: { borderColor: "rgb(34, 197, 94)" },
  to: { borderColor: "transparent" },
});

/** Délai entre la détection d'un QR valide et le push du sheet suivant. Juste
 *  assez pour que la bordure verte + le check circle soient perçus, mais
 *  suffisamment court pour que le slide du `BottomSheet` reste fluide. */
const POST_SCAN_TRANSITION_DELAY_MS = 600;

type QuickModuleAccessProps = {
  /**
   * Callback déclenché lorsqu'un module valide est scanné. Utilisé par le
   * sheet hôte pour pousser un `MODULE_ACCESS` sur la stack.
   */
  onScanned: (areaId: string, moduleAddress: string) => void;
};

const QuickModuleAccess = ({ onScanned }: QuickModuleAccessProps) => {
  const { t } = useTranslation();
  const { data: areas } = useAreas();

  const [scannedAddress, setScannedAddress] = useState<number | null>(null);
  const [showGreenBorder, setShowGreenBorder] = useState(false);
  const [showCheckCircle, setShowCheckCircle] = useState(false);

  // react-qr-reader@3.0.0-beta-1 ne nettoie pas son setInterval de scan au
  // démontage, donc onResult continue à être appelé toutes les 500ms même après
  // qu'on ait quitté la page. On utilise un ref de montage pour court-circuiter
  // la closure captée par la lib, et on coupe nous-même le flux caméra.
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;

      const video = document.getElementById(
        "qrScanner"
      ) as HTMLVideoElement | null;
      if (video?.srcObject instanceof MediaStream) {
        video.srcObject.getTracks().forEach((track) => track.stop());
        video.srcObject = null;
      }
    };
  }, []);

  // Quand une adresse est scannée, on cherche la parcelle qui contient ce
  // module et on délègue la transition au sheet hôte. La transition visuelle
  // entre les deux sheets est gérée par `AnimatePresence` du `BottomSheet`,
  // pas besoin d'overlay plein écran ici.
  useEffect(() => {
    if (scannedAddress === null || !areas) return;

    const matchingArea = areas.find((area) =>
      area.posts?.some((post) => post.address === scannedAddress)
    );

    if (!matchingArea) {
      showFailureToast("quickModuleAccess.moduleDoesNotExist", t);
      return;
    }

    toast.getToasts().forEach((toastElement) => toast.dismiss(toastElement.id));

    const transitionTimer = setTimeout(() => {
      onScanned(matchingArea._id, String(scannedAddress));
    }, POST_SCAN_TRANSITION_DELAY_MS);

    return () => {
      clearTimeout(transitionTimer);
    };
  }, [scannedAddress, areas]);

  return (
    <div tw="px-4 pb-6">
      <div tw="max-w-xl mx-auto">
        <div tw="isolate relative rounded-3xl overflow-hidden bg-black">
          {showCheckCircle && (
            <div tw="absolute inset-0 flex items-center justify-center z-10">
              <CheckCircleIcon tw="w-10 h-10 text-green-500" />
            </div>
          )}
          <QrReader
            constraints={{
              aspectRatio: { ideal: 1 },
              facingMode: "environment",
            }}
            videoId="qrScanner"
            ViewFinder={(props) => (
              <Mask {...props} showGreenBorder={showGreenBorder} />
            )}
            scanDelay={500}
            onResult={async (result) => {
              if (!isMountedRef.current) return;
              if (!result) return;
              const text = result?.getText();
              if (text === undefined || text === null) return;

              const lastDigitsMatch = text.match(/(\d+)(?!.*\d)/);
              if (!lastDigitsMatch) return;

              setScannedAddress(Number(lastDigitsMatch[1]));
              setShowGreenBorder(true);
              setShowCheckCircle(true);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default QuickModuleAccess;

type MaskProps = ComponentPropsWithoutRef<"svg"> & {
  color?: string;
  showGreenBorder?: boolean;
};

const Mask = ({
  color = "currentColor",
  showGreenBorder,
  ...props
}: MaskProps) => (
  <svg
    viewBox="0 0 100 100"
    css={[
      tw`absolute inset-0 z-10 border-black/40 border-[2rem] text-primary-200`,
      showGreenBorder && {
        borderColor: "rgb(34, 197, 94)",
        animationName: greenBorderFade,
        animationDuration: "8s",
        animationTimingFunction: "ease-out",
        animationFillMode: "forwards",
      },
    ]}
    {...props}
  >
    <path fill="none" d="M13,0 L0,0 L0,13" stroke={color} strokeWidth="5" />
    <path fill="none" d="M0,87 L0,100 L13,100" stroke={color} strokeWidth="5" />
    <path
      fill="none"
      d="M87,100 L100,100 L100,87"
      stroke={color}
      strokeWidth="5"
    />
    <path fill="none" d="M100,13 L100,0 87,0" stroke={color} strokeWidth="5" />
  </svg>
);
